import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import TeamTemplateSelector from '@/components/teams/TeamTemplateSelector'
import { teamStore } from '@/stores/TeamStore'
import {
  UsersIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PlayIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  EyeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import type { AgentTeam, TeamCoordinationStrategy, TeamStatus } from '@/types'

const TeamsPage = observer(() => {
  const navigate = useNavigate()
  const [showTemplates, setShowTemplates] = useState(false)

  useEffect(() => {
    teamStore.clearFilters()
    teamStore.setPage(1)
    teamStore.fetchTeams()
  }, [])

  const getStrategyBadgeVariant = (strategy: TeamCoordinationStrategy) => {
    const variants: Record<TeamCoordinationStrategy, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      SEQUENTIAL: 'primary',
      PARALLEL: 'success',
      HIERARCHICAL: 'warning',
      CONSENSUS: 'error',
      FEEDBACK_LOOP: 'gray',
    }
    return variants[strategy]
  }

  const getStatusBadgeVariant = (status: TeamStatus) => {
    const variants: Record<TeamStatus, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      DRAFT: 'gray',
      ACTIVE: 'success',
      INACTIVE: 'warning',
      ARCHIVED: 'error',
    }
    return variants[status]
  }

  const getStrategyLabel = (strategy: TeamCoordinationStrategy) => {
    const labels: Record<TeamCoordinationStrategy, string> = {
      SEQUENTIAL: 'Sequential',
      PARALLEL: 'Parallel',
      HIERARCHICAL: 'Hierarchical',
      CONSENSUS: 'Consensus',
      FEEDBACK_LOOP: 'Feedback Loop',
    }
    return labels[strategy]
  }

  const handleDeleteTeam = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete team "${name}"?`)) {
      try {
        await teamStore.deleteTeam(id)
      } catch (error) {
        console.error('Failed to delete team:', error)
      }
    }
  }

  const handleExecuteTeam = (team: AgentTeam) => {
    navigate(`/teams/${team.id}/execute`)
  }

  // If showing templates, render template selector
  if (showTemplates) {
    return (
      <TeamTemplateSelector
        onClose={() => setShowTemplates(false)}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Teams</h1>
          <p className="text-muted-foreground mt-1">
            Manage multi-agent teams and orchestration ({teamStore.totalTeams} total)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setShowTemplates(true)}
          >
            <SparklesIcon className="w-5 h-5 mr-2" />
            Browse Templates
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate('/teams/new')}
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Team
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={teamStore.searchQuery}
                  onChange={(e) => teamStore.setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => teamStore.clearFilters()}
              disabled={!teamStore.hasActiveFilters}
            >
              <FunnelIcon className="w-5 h-5 mr-2" />
              Clear Filters
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <select
              value={teamStore.strategyFilter}
              onChange={(e) =>
                teamStore.setStrategyFilter(e.target.value as TeamCoordinationStrategy | 'all')
              }
              className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Strategies</option>
              <option value="SEQUENTIAL">Sequential</option>
              <option value="PARALLEL">Parallel</option>
              <option value="HIERARCHICAL">Hierarchical</option>
              <option value="CONSENSUS">Consensus</option>
              <option value="FEEDBACK_LOOP">Feedback Loop</option>
            </select>

            <select
              value={teamStore.statusFilter}
              onChange={(e) =>
                teamStore.setStatusFilter(e.target.value as TeamStatus | 'all')
              }
              className="px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Team List */}
      {teamStore.isLoading ? (
        <Card className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading teams...</p>
        </Card>
      ) : teamStore.error ? (
        <Card className="text-center py-12">
          <p className="text-red-600">{teamStore.error}</p>
          <Button
            variant="secondary"
            onClick={() => teamStore.fetchTeams()}
            className="mt-4"
          >
            Retry
          </Button>
        </Card>
      ) : teamStore.filteredTeams.length === 0 ? (
        <Card className="text-center py-12">
          <UsersIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {teamStore.hasActiveFilters ? 'No teams found' : 'No teams yet'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {teamStore.hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Get started by creating your first multi-agent team'}
          </p>
          {!teamStore.hasActiveFilters && (
            <Button variant="primary" onClick={() => navigate('/teams/new')}>
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Team
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teamStore.filteredTeams.map((team) => (
            <Card key={team.id} className="hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-500/15 rounded-lg">
                    <UsersIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{team.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {team.members.length} member{team.members.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <Badge variant={getStatusBadgeVariant(team.status)}>
                  {team.status}
                </Badge>
              </div>

              {team.description && (
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {team.description}
                </p>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Strategy:</span>
                  <Badge variant={getStrategyBadgeVariant(team.strategy)}>
                    {getStrategyLabel(team.strategy)}
                  </Badge>
                </div>

                {team.feedback_enabled && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Feedback:</span>
                    <span className="text-foreground font-medium">
                      Max {team.max_iterations || 3} iterations
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="text-foreground">
                    {formatDistanceToNow(new Date(team.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-border">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleExecuteTeam(team)}
                  className="flex-1"
                  disabled={team.status !== 'ACTIVE'}
                >
                  <PlayIcon className="w-4 h-4 mr-1" />
                  Execute
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/teams/${team.id}`)}
                >
                  <EyeIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/teams/${team.id}/edit`)}
                >
                  <PencilIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteTeam(team.id, team.name)}
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!teamStore.isLoading &&
        !teamStore.error &&
        teamStore.totalTeams > teamStore.pageSize && (
          <Card>
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(teamStore.currentPage - 1) * teamStore.pageSize + 1} to{' '}
                {Math.min(teamStore.currentPage * teamStore.pageSize, teamStore.totalTeams)} of{' '}
                {teamStore.totalTeams} teams
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => teamStore.setPage(teamStore.currentPage - 1)}
                  disabled={teamStore.currentPage === 1}
                >
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {teamStore.currentPage} of{' '}
                  {Math.ceil(teamStore.totalTeams / teamStore.pageSize)}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => teamStore.setPage(teamStore.currentPage + 1)}
                  disabled={
                    teamStore.currentPage >=
                    Math.ceil(teamStore.totalTeams / teamStore.pageSize)
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        )}
    </div>
  )
})

export default TeamsPage
